import { useState } from "react";
import type {
  InvoiceUploadInput,
  PurchaseOrderPage,
} from "@shared-core/data/supplier-portal-adapter";
import {
  ACTION_WORDING,
  INVOICE_WORDING,
  PAGE_WORDING,
  dayMonth,
  invoiceStatus,
  pounds,
} from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../../app/components/primitives/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { InvoiceQueryCard } from "../../app/components/domain/InvoiceQueryCard";
import { InvoiceUploadForm } from "../../app/components/domain/InvoiceUploadForm";
import { StatusPill } from "../../app/components/domain/StatusPill";
import { StaffViewReadOnly } from "../../app/components/domain/StaffViewReadOnly";

/**
 * The order's invoices with their status from our ledger, and the upload once a line has gone (and, for a closed
 * order, until its invoice window ends).
 */
export function InvoiceSection({
  page,
  isUploadOpen,
  onUploadOpenChange,
  onUpload,
  onReplyToQuery,
}: {
  page: PurchaseOrderPage;
  isUploadOpen: boolean;
  onUploadOpenChange: (open: boolean) => void;
  onUpload: (input: InvoiceUploadInput) => Promise<void>;
  /** A finance query on one of this order's invoices: answerable here, also in the closed window (spec 06). */
  onReplyToQuery: (
    invoiceRef: string,
    input: { text: string; attachment: File | null },
  ) => Promise<void>;
}) {
  const [justSent, setJustSent] = useState(false);
  const { invoices, can_upload: canUpload } = page.invoicing;
  return (
    <div className="grid gap-3">
      {invoices
        .filter((invoice) => invoice.query?.can_reply)
        .map((invoice) => (
          <InvoiceQueryCard
            key={invoice.invoice_ref}
            invoice={invoice}
            onReply={(input) => onReplyToQuery(invoice.invoice_ref, input)}
          />
        ))}
      {invoices.length > 0 && (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Your invoice</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Received</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.invoice_ref}>
                  <TableCell className="font-mono text-xs">
                    {invoice.invoice_ref}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {pounds(invoice.total_inc_vat)}
                  </TableCell>
                  <TableCell>{dayMonth(invoice.received_on)}</TableCell>
                  <TableCell>
                    <StatusPill {...invoiceStatus(invoice)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {justSent && !isUploadOpen && (
        <p role="status" className="text-sm font-semibold text-green-700">
          ✓ {INVOICE_WORDING.sent}
        </p>
      )}
      {!canUpload && invoices.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {page.status === "cancelled"
            ? PAGE_WORDING.cancelledNoInvoice
            : PAGE_WORDING.invoiceNotYet(page.service)}
        </p>
      )}
      {canUpload &&
        (isUploadOpen ? (
          <div className="grid max-w-md gap-2 rounded-lg border bg-card p-4">
            <p className="font-semibold">{INVOICE_WORDING.uploadTitle}</p>
            <InvoiceUploadForm
              idPrefix="po-invoice"
              choices={[page.purchase_order_no]}
              fixedPurchaseOrderNo={page.purchase_order_no}
              today={page.today}
              onSubmit={async (input) => {
                await onUpload(input);
                setJustSent(true);
                onUploadOpenChange(false);
              }}
            />
            <Button
              variant="ghost"
              className="justify-self-start"
              onClick={() => onUploadOpenChange(false)}
            >
              {ACTION_WORDING.cancel}
            </Button>
          </div>
        ) : (
          // A closed order's Upload invoice sits under its banner, at the top of the page.
          page.status !== "closed" && (
            <StaffViewReadOnly>
              <div>
                <Button
                  variant="outline"
                  onClick={() => {
                    setJustSent(false);
                    onUploadOpenChange(true);
                  }}
                >
                  {ACTION_WORDING.uploadInvoice}
                </Button>
              </div>
            </StaffViewReadOnly>
          )
        ))}
    </div>
  );
}
